import IconLink from "@shared/components/icons/Link"
import { Button } from "@shared/components/untitledui/base/buttons/button"
import { Tooltip } from "@shared/components/untitledui/base/tooltip/tooltip"
import { cx } from "@shared/components/untitledui/util/cx"
import type { LinkDialogOrigin } from "@shared/unified-tiptap/extensions/link/link-inline-extension"
import { modKeyText } from "@shared/unified-tiptap/util/is-apple"
import type { Editor } from "@tiptap/core"
import { useEditorState } from "@tiptap/react"
import { TOOLBAR_BUTTON_ACTIVE, TOOLBAR_BUTTON_CHROME } from "./toolbar-button-chrome"

export function LinkButton({
  editor,
  openLinkDialog,
}: {
  editor: Editor
  openLinkDialog: (origin: LinkDialogOrigin) => void
}) {
  const { isActive, canSet } = useEditorState({
    editor,
    selector: (snapshot) => ({
      isActive: snapshot.editor.isActive("linkInline") || snapshot.editor.isActive("linkBlock"),
      // The dialog inserts an inline token, so the button is usable wherever the
      // schema allows one (not in titles, which are text-only).
      canSet:
        snapshot.editor.isEditable &&
        snapshot.editor.state.selection.$from.parent.type.contentMatch.matchType(
          snapshot.editor.schema.nodes.linkInline,
        ) !== null,
    }),
  })

  return (
    <Tooltip
      title={
        <>
          Link
          <span className="ml-2 text-gray-400">{modKeyText + "+K"}</span>
        </>
      }
      closeDelay={0}
    >
      <Button
        color="tertiary"
        size="xs"
        isDisabled={!(isActive || canSet)}
        className={cx(TOOLBAR_BUTTON_CHROME, isActive && TOOLBAR_BUTTON_ACTIVE)}
        // The button opens a dialog rather than toggling, so it isn't a ToggleButton; the
        // "caret is on a link" state reaches assistive tech through the action name instead
        // of only the highlight className.
        aria-label={isActive ? "Edit link" : "Insert link"}
        onPress={() => openLinkDialog("floating-toolbar")}
        iconLeading={<IconLink />}
      />
    </Tooltip>
  )
}
